// src/api.ts
const TOKEN_KEY = "elume_token";
const DEMO_TOKEN_KEY = "elume_demo_token";
const DEMO_PROVISION_KEY = "elume_demo_provision_key";
export const INVITATION_LOGIN_NOTICE_KEY = "elume_invitation_login_notice";
type JsonBody = BodyInit | Record<string, unknown> | unknown[] | null;
type ApiRequestInit = Omit<RequestInit, "body"> & { body?: JsonBody };
export type StudentPracticeQuizListItem = {
  id: number;
  title: string;
  class_id: number;
  class_name?: string | null;
  question_count: number;
  scope?: "class" | "global";
  source_label?: string | null;
  subject?: string | null;
  level?: string | null;
  course_level?: string | null;
  paper?: string | null;
  topic?: string | null;
};
export type StudentPracticeQuizQuestion = { id: number; prompt: string; choices: string[]; position?: number };
export type StudentPracticeQuiz = { id: number; title: string; class_id: number; class_name?: string | null; scope?: "class" | "global"; source_label?: string | null; subject?: string | null; level?: string | null; course_level?: string | null; paper?: string | null; topic?: string | null; questions: StudentPracticeQuizQuestion[] };
export type StudentPracticeQuizAnswer = { question_id: number; selected_index: number };
export type StudentPracticeQuizSubmission = {
  total_questions: number;
  correct_count: number;
  percentage: number;
  questions: Array<{ question_id: number; selected_index: number; correct: boolean; correct_index: number; explanation?: string | null }>;
};
const FRIENDLY_AUTH_ERROR =
  "Your session has expired or changed. Please log in again to continue. This can happen for security reasons or if you signed in on another device.";

export class ApiError extends Error {
  status: number;
  response: unknown;
  retryAfterSeconds: number | null;

  constructor(status: number, message: string, response: unknown, retryAfterSeconds: number | null = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.response = response;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export function getToken(): string | null {
  try {
    // A demo token lives only in this browser tab.  It never overwrites an
    // existing teacher login stored in localStorage.
    return sessionStorage.getItem(DEMO_TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // ignore
  }
}

export function setDemoToken(token: string) {
  try {
    sessionStorage.setItem(DEMO_TOKEN_KEY, token);
  } catch {
    // ignore
  }
}

export function clearDemoToken() {
  try {
    // Never clear the normal account token while recovering an expired or
    // invalid tab-scoped demo identity.
    sessionStorage.removeItem(DEMO_TOKEN_KEY);
  } catch {
    // ignore
  }
}

export function getOrCreateDemoProvisionKey(): string {
  try {
    const existing = sessionStorage.getItem(DEMO_PROVISION_KEY);
    if (existing) return existing;
    const key = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID().replace(/-/g, "")
      : Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
    sessionStorage.setItem(DEMO_PROVISION_KEY, key);
    return key;
  } catch {
    // The API remains safe without browser storage; recovery just cannot span
    // a failed navigation in browsers that forbid sessionStorage.
    return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
  }
}

export function clearDemoProvisionKey() {
  try { sessionStorage.removeItem(DEMO_PROVISION_KEY); } catch { /* ignore */ }
}

export function isDemoSession() {
  try {
    return Boolean(sessionStorage.getItem(DEMO_TOKEN_KEY));
  } catch {
    return false;
  }
}

export function clearToken() {
  try {
    if (sessionStorage.getItem(DEMO_TOKEN_KEY)) sessionStorage.removeItem(DEMO_TOKEN_KEY);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore
  }
}

function isAuthLikeError(status: number, message: string) {
  if (status === 401) return true;
  const text = message.trim().toLowerCase();
  if (!text) return false;
  return (
    text.includes("invalid token") ||
    text.includes("token expired") ||
    text.includes("expired token") ||
    text.includes("unauthorized") ||
    text.includes("unauthorised") ||
    text.includes("not authenticated") ||
    text.includes("authentication required") ||
    text.includes("session expired") ||
    text.includes("jwt expired") ||
    text.includes("invalid signature")
  );
}

function normaliseApiErrorMessage(status: number, message: string) {
  if (isAuthLikeError(status, message)) {
    return FRIENDLY_AUTH_ERROR;
  }
  return message;
}

export async function apiFetch(path: string, init: ApiRequestInit = {}) {
  const token = getToken();
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;

  const headers = new Headers(init.headers || {});
  if (!headers.has("Content-Type") && init.body && !isFormData) {
    headers.set("Content-Type", "application/json");
  }
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const url =
    path.startsWith("/api")
      ? path
      : `/api${path.startsWith("/") ? "" : "/"}${path}`;

  const body: BodyInit | null | undefined =
    init.body && !isFormData && typeof init.body !== "string"
      ? JSON.stringify(init.body)
      : (init.body as BodyInit | null | undefined);

  const res = await fetch(url, { ...init, headers, body });

  const text = await res.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
    const detail = data && typeof data === "object" ? (data.detail || data.message) : data;
    const restrictionCode = detail && typeof detail === "object" ? detail.code : null;
    if (typeof restrictionCode === "string" && restrictionCode.startsWith("demo_")) {
      window.dispatchEvent(new CustomEvent("elume-demo-restricted", {
        detail: { message: typeof detail.message === "string" ? detail.message : null },
      }));
    }
    const rawMessage =
      (typeof detail === "string" && detail) ||
      (detail && typeof detail === "object" && typeof detail.message === "string" && detail.message) ||
      `Request failed (${res.status})`;
    // Never surface a reverse-proxy HTML document in product UI. Individual
    // screens can provide a localized recovery message for this transport
    // failure while the API error still retains its status for callers.
    const msg = /^\s*<(?:!doctype|html)/i.test(rawMessage)
      ? `Request failed (${res.status})`
      : rawMessage;
    const retryAfterHeader = Number(res.headers.get("Retry-After"));
    const retryAfterBody = detail && typeof detail === "object" ? Number(detail.retry_after_seconds) : NaN;
    const retryAfterSeconds = Number.isFinite(retryAfterHeader) && retryAfterHeader > 0
      ? retryAfterHeader
      : (Number.isFinite(retryAfterBody) && retryAfterBody > 0 ? retryAfterBody : null);
    throw new ApiError(res.status, normaliseApiErrorMessage(res.status, msg), data, retryAfterSeconds);
  }

  return data;
}

export async function getStudentPracticeQuizzes(token: string): Promise<StudentPracticeQuizListItem[]> {
  const data = await apiFetch(`/student/${encodeURIComponent(token)}/practice-quizzes`);
  return Array.isArray(data) ? data as StudentPracticeQuizListItem[] : [];
}

export async function getPublicPracticeLibraryQuizzes(): Promise<StudentPracticeQuizListItem[]> {
  const data = await apiFetch("/practice-library/quizzes");
  return Array.isArray(data) ? data as StudentPracticeQuizListItem[] : [];
}

export async function getPublicPracticeLibraryQuiz(quizId: number): Promise<StudentPracticeQuiz> {
  return apiFetch(`/practice-library/quizzes/${encodeURIComponent(String(quizId))}`) as Promise<StudentPracticeQuiz>;
}

export async function getStudentPracticeQuiz(token: string, quizId: number, scope: "class" | "global" = "class"): Promise<StudentPracticeQuiz> {
  return apiFetch(`/student/${encodeURIComponent(token)}/practice-quizzes/${encodeURIComponent(String(quizId))}?scope=${scope}`) as Promise<StudentPracticeQuiz>;
}

export async function submitStudentPracticeQuiz(token: string, quizId: number, answers: StudentPracticeQuizAnswer[], scope: "class" | "global" = "class"): Promise<StudentPracticeQuizSubmission> {
  return apiFetch(`/student/${encodeURIComponent(token)}/practice-quizzes/${encodeURIComponent(String(quizId))}/submit?scope=${scope}`, {
    method: "POST",
    body: { answers },
  }) as Promise<StudentPracticeQuizSubmission>;
}

export async function submitPublicPracticeLibraryQuiz(quizId: number, answers: StudentPracticeQuizAnswer[]): Promise<StudentPracticeQuizSubmission> {
  return apiFetch(`/practice-library/quizzes/${encodeURIComponent(String(quizId))}/submit`, {
    method: "POST",
    body: { answers },
  }) as Promise<StudentPracticeQuizSubmission>;
}

export async function apiFetchBlob(path: string, init: ApiRequestInit = {}) {
  const token = getToken();
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;

  const headers = new Headers(init.headers || {});
  if (!headers.has("Content-Type") && init.body && !isFormData) {
    headers.set("Content-Type", "application/json");
  }
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const body: BodyInit | null | undefined =
    init.body && !isFormData && typeof init.body !== "string"
      ? JSON.stringify(init.body)
      : (init.body as BodyInit | null | undefined);

  const url =
    path.startsWith("/api")
      ? path
      : `/api${path.startsWith("/") ? "" : "/"}${path}`;

  const res = await fetch(url, { ...init, headers, body });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(normaliseApiErrorMessage(res.status, text || `Request failed (${res.status})`));
  }

  return res.blob();
}

export async function openProtectedFileInNewTab(path: string) {
  if (!path) throw new Error("Missing file path");

  if (path.startsWith("http://") || path.startsWith("https://")) {
    window.open(path, "_blank", "noopener,noreferrer");
    return;
  }

  const url =
    path.startsWith("/api")
      ? path
      : "/api" + (path.startsWith("/") ? "" : "/") + path;

  // iOS Safari treats a window opened after an awaited fetch as a popup. Open
  // a blank viewer synchronously from the original tap, then navigate it once
  // the protected attachment has been fetched.
  const viewer = window.open("", "_blank");
  if (viewer) viewer.opener = null;

  try {
    const blob = await apiFetchBlob(url, { method: "GET" });
    const objectUrl = URL.createObjectURL(blob);
    if (viewer) viewer.location.href = objectUrl;
    else window.open(objectUrl, "_blank", "noopener,noreferrer");

    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch (error) {
    viewer?.close();
    throw error;
  }
}

const STUDENT_PREVIEW_MIME_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]);
const studentPreviewWindows = new Map<Window, string>();
let studentPreviewWindowMonitor: number | null = null;

export class StudentPreviewUnavailableError extends Error {
  constructor() {
    super("This file type cannot be previewed in the browser.");
    this.name = "StudentPreviewUnavailableError";
  }
}

function monitorStudentPreviewWindows() {
  if (studentPreviewWindowMonitor !== null) return;
  studentPreviewWindowMonitor = window.setInterval(() => {
    for (const [viewer, objectUrl] of studentPreviewWindows) {
      if (!viewer.closed) continue;
      URL.revokeObjectURL(objectUrl);
      studentPreviewWindows.delete(viewer);
    }
    if (studentPreviewWindows.size === 0 && studentPreviewWindowMonitor !== null) {
      window.clearInterval(studentPreviewWindowMonitor);
      studentPreviewWindowMonitor = null;
    }
  }, 2000);
}

/** Opens only browser-safe document/image types using the current tab's protected fetch.
 * The blank viewer is opened synchronously to avoid popup blockers; its blob URL
 * remains alive until the viewer tab closes so native PDF viewers can finish loading.
 */
export async function openStudentResourcePreview(path: string) {
  if (!path) throw new Error("Missing file path");
  const viewer = window.open("", "_blank");
  if (!viewer) throw new Error("The browser blocked the new tab. Allow pop-ups for Elume and try again.");
  viewer.opener = null;

  try {
    const blob = await apiFetchBlob(path, { method: "GET" });
    const mimeType = blob.type.split(";", 1)[0].trim().toLowerCase();
    if (!STUDENT_PREVIEW_MIME_TYPES.has(mimeType)) throw new StudentPreviewUnavailableError();
    const objectUrl = URL.createObjectURL(blob);
    studentPreviewWindows.set(viewer, objectUrl);
    monitorStudentPreviewWindows();
    viewer.location.href = objectUrl;
    return mimeType;
  } catch (error) {
    viewer.close();
    throw error;
  }
}
